import { css } from '@emotion/react'
import { Alert, Button, HFlow, Theme, Tooltip, useTheme } from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { DateTime } from 'components/date'
import { CheckboxField, SubmitButton } from 'components/form'
import { usePecField } from 'components/form/core/hooks/useField'
import { PageContent } from 'components/layout/PageContent'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useEffect } from 'react'
import { FormSpy, useForm } from 'react-final-form'

import { meta, SoapState } from '../atendimento-individual/model'
import { computeFinalizarAtendimentoButtonLabel } from '../utils/computeFinalizarAtendimentoButtonLabel'
import { atendimentoImpressaoMessage } from './atendimentoImpressaoMessage'
import { ManterCidadaoLista } from './soap/finalizacao/components/DesfechoAtendimentoPanel'

interface AtendimentoIndividualFooterProps {
  nomeAtendimento: string
  handleSubmit: () => void
  handleCancel: (isStartObservacao: boolean) => void
  cancelButtonLabel: string
  isLoadingCancelar: boolean
}

export const AtendimentoIndividualFooter = (props: AtendimentoIndividualFooterProps) => {
  const { nomeAtendimento, handleSubmit, handleCancel, cancelButtonLabel, isLoadingCancelar } = props

  const theme = useTheme()
  const classes = createStyles(theme)
  const { analytics } = useAnalytics()
  const { isEstagio } = useSession()

  const {
    input: { value: isStartObservacao },
  } = usePecField({ name: meta.plano?.startObservacao, subscription: { value: true } })
  const {
    input: { value: manterCidadaoNaLista },
  } = usePecField({ name: meta.finalizacao?.desfechoAtendimento?.manterCidadaoLista, subscription: { value: true } })
  const {
    input: { value: imprimirAtendimento },
  } = usePecField({ name: meta.imprimirAtendimento, subscription: { value: true } })

  const {
    mutators: { changeValue },
  } = useForm<SoapState>()

  const {
    observacao: { isAtendimentoObservacao },
    statusRevisaoAtendimento,
    isRetificacao,
  } = useAtendimentoContext()

  const isObservacao = !!isStartObservacao || isAtendimentoObservacao
  const isFinalizandoObservacao = isObservacao && manterCidadaoNaLista === ManterCidadaoLista.NAO
  const desabilitarImpressaoFinalizacao = manterCidadaoNaLista === ManterCidadaoLista.SIM && isObservacao

  const submitButtonLabel = computeFinalizarAtendimentoButtonLabel({
    isEstagio,
    statusRevisaoAtendimento,
    tipoAtendimentoText: nomeAtendimento,
    isFinalizandoObservacao,
    isStartingObservacao: isStartObservacao,
    isAtendimentoObservacao,
    isRetificacao,
  })

  useEffect(() => {
    if (desabilitarImpressaoFinalizacao && !!imprimirAtendimento) {
      changeValue(meta.imprimirAtendimento, false)
    }
  }, [changeValue, desabilitarImpressaoFinalizacao, imprimirAtendimento])

  return (
    <div css={classes.actions}>
      <PageContent type='filled' fluid>
        <HFlow justifyContent='space-between'>
          <FormSpy<SoapState> subscription={{ dirty: true, active: true, values: true }}>
            {({ values }) => (
              <Alert inline type='success' styles={{ wrapper: classes.draftAlertWrapper }}>
                Rascunho salvo automaticamente às <DateTime format='HH:mm' value={values.lastSaved} />
              </Alert>
            )}
          </FormSpy>
          <HFlow>
            {!isEstagio && (
              <Tooltip
                text={
                  desabilitarImpressaoFinalizacao &&
                  'A impressão do atendimento de observação só pode ocorrer ao liberar o cidadão.'
                }
              >
                <CheckboxField
                  name={meta.imprimirAtendimento}
                  label={atendimentoImpressaoMessage(isRetificacao)}
                  disabled={desabilitarImpressaoFinalizacao}
                  style={classes.print}
                  onClick={() => analytics.logEvent('click_imprimir_finalizar')}
                />
              </Tooltip>
            )}
            <Button
              kind='normal'
              onClick={() => handleCancel(isStartObservacao)}
              loading={isLoadingCancelar}
              disabled={isLoadingCancelar}
            >
              {cancelButtonLabel}
            </Button>
            <SubmitButton kind='primary' handleSubmit={handleSubmit} dataTestId='AtendimentoIndividualFooter.finalizar'>
              {submitButtonLabel}
            </SubmitButton>
          </HFlow>
        </HFlow>
      </PageContent>
    </div>
  )
}

const createStyles = (theme: Theme) => ({
  draftAlertWrapper: css`
    background-color: transparent;
    border-width: 0;
  `,
  actions: css`
    border-top: 1px solid ${theme.pallete.divider};
    height: 6.125rem;
  `,
  print: css`
    border: 1px solid ${theme.pallete.gray.c80};
    border-radius: 2px;
    padding: 0.75rem 1rem;
  `,
})
